import { css } from "@emotion/css";
import { type VirtualTableColumn, Button, Tooltip } from "@snapchat/snap-design-system";
import { ExternalLinkIcon } from "@snapchat/snap-design-system-icons";
import { Header } from "../../common/components/table/Header";
import { DecreaseText } from "../../common/components/text";
import { AppListItemModel } from "../models";
import { InfoTooltip } from "../../common/components";
import { Icon, Figure } from "../styles/AppListColumns.styles";
import { AppPlatform } from "../../gql-generated";
import { AppStatusCell } from "./AppStatusCell";

// Unfortunately SDS doesn't export this interface
interface CellRenderer {
    row: {
        index: number;
    };
    data: Readonly<AppListItemModel[]>;
}

function getRenderer(fn: (item: AppListItemModel) => JSX.Element) {
    return ({ row, data }: CellRenderer) => fn(data[row.index] || new AppListItemModel());
}

export function getColumns(): VirtualTableColumn<AppListItemModel>[] {
    return [
        {
            id: "icon",
            width: 72,
            Cell: getRenderer(({ iconUrl }) => (
                <Figure>
                    <Icon src={iconUrl} />
                </Figure>
            )),
        },
        {
            id: "name",
            Header: <Header>Name</Header>,
            Cell: getRenderer(({ name }) => <DecreaseText>{name}</DecreaseText>),
        },
        {
            id: "platform",
            width: 100,
            Header: <DecreaseText className={textWrapper}>Platform</DecreaseText>,
            Cell: getRenderer(({ platformType }) =>
                platformType ? <DecreaseText>{AppPlatform[platformType]}</DecreaseText> : <DecreaseText />,
            ),
        },
        {
            id: "prodStatus",
            width: 185,
            Header: (
                <DecreaseText className={textWrapper}>
                    Production
                    <InfoTooltip content="The status of whether Camera Kit is enabled on a version in production." />
                </DecreaseText>
            ),
            Cell: getRenderer(({ prodStatus, prodLiveVersion }) => (
                <AppStatusCell status={prodStatus} content={prodLiveVersion} />
            )),
        },
        {
            id: "stageStatus",
            width: 185,
            Header: (
                <DecreaseText className={textWrapper}>
                    Staging
                    <InfoTooltip content="The status of whether Camera Kit is enabled on a version in staging." />
                </DecreaseText>
            ),
            Cell: getRenderer(({ stageStatus, stagingLiveVersion }) => (
                <AppStatusCell status={stageStatus} content={stagingLiveVersion} />
            )),
        },
        {
            id: "id",
            width: 75,
            Cell: getRenderer(({ appId }) => (
                <a href={`https://kit.snapchat.com/manage/apps/${appId}`} target="_blank">
                    <input type="hidden" value={appId} data-testid="link-out-id" />
                    <Tooltip content="View in Developer Portal">
                        <Button shape="circle" onClick={(event) => event.stopPropagation()}>
                            <ExternalLinkIcon />
                        </Button>
                    </Tooltip>
                </a>
            )),
        },
    ];
}

const textWrapper = css`
    display: flex;
`;
